import { useState, useEffect, type MouseEvent } from "react";
import { useLocation } from "wouter";
import { Helmet } from "react-helmet-async";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { IntroConsultationCard } from "@/components/IntroConsultationCard";
import { Clock, Heart, ArrowUp, User } from "lucide-react";
import NavigationBanner from "@/components/NavigationBanner";
import { LocalBusinessSchema, ServiceSchema, PersonSchema, BreadcrumbSchema } from "@/components/SEOSchema";
import { HreflangTags } from "@/components/HreflangTags";
import { blogPosts } from "@/data/blogPosts";
import { storeBookingSelection, type ConsultationType } from "@/lib/bookingSelection";

import { SESSION_PRICE_INTRO, SESSION_PRICE_ONLINE, SESSION_PRICE_PRESENCE } from "@/lib/pricing";
import { CANONICAL_ROUTES, SITE_BASE_URL, buildBlogArticleRoute, buildCanonicalUrl, getLazyLoading } from "@/lib/utils";

export default function Home() {
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [, setLocation] = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 400);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    // Handle scroll-to-section stored in sessionStorage (from other pages)
    const sectionId = sessionStorage.getItem('scrollToSection');
    if (sectionId) {
      sessionStorage.removeItem('scrollToSection');
      setTimeout(() => {
        scrollToSection(sectionId);
      }, 100);
    } else {
      // Scroll to top when navigating to home without a specific section
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      if (id === "home") {
        // Per home, scrolla all'inizio della pagina
        window.scrollTo({ top: 0, behavior: "smooth" });
      } else {
        // Per altre sezioni, scrolla rispettando l'offset della navbar
        const navHeight = 96; // navbar height (6rem)
        const elementTop = element.getBoundingClientRect().top + window.scrollY;
        const targetScroll = elementTop - navHeight;
        window.scrollTo({ top: targetScroll, behavior: "smooth" });
      }
    }
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const navigateToBooking = (type: ConsultationType, lock: boolean) => {
    storeBookingSelection({ type, lock });
    setLocation(CANONICAL_ROUTES.prenota);
  };

  const isModifiedClick = (event: MouseEvent<HTMLAnchorElement>) =>
    event.defaultPrevented || event.button !== 0 || event.metaKey || event.altKey || event.ctrlKey || event.shiftKey;

  const handleAnchorNavigation = (event: MouseEvent<HTMLAnchorElement>, action: () => void) => {
    if (isModifiedClick(event)) {
      return;
    }

    event.preventDefault();
    action();
  };

  const homeHrefForSection = (id: "home" | "counselling" | "servizi") =>
    id === "home" ? CANONICAL_ROUTES.home : `${CANONICAL_ROUTES.home}#${id}`;
  const featuredBlogPosts = blogPosts.slice(0, 2);
  const imageLoading = getLazyLoading();
  const canonicalUrl = buildCanonicalUrl(CANONICAL_ROUTES.home);

  return (
    <div className="min-h-screen">
      <Helmet>
        <title>Counselling Professionale in Italiano | Aura Counselling</title>
        <meta
          name="description"
          content="Counselling gratuito in italiano online e in presenza a Zurigo con sessione introduttiva gratuita. Sostegno psicologico in italiano a Zurigo e in tutta la Svizzera con supporto personalizzato per il tuo benessere."
        />
        <meta
          name="keywords"
          content="counselling online, counselling online in italiano, counselling online italiano, counselling gratuito, counselling gratuito online, counselling gratuito in italiano, counselling gratuito Svizzera, counselling a Zurigo, counselling Zurigo in italiano, counselling Svizzera, counselling Svizzera in italiano, counselling Ticino, counselling Ticino in italiano, sostegno psicologico online, sostegno psicologico in italiano a Zurigo"
        />
        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Counselling Professionale in Italiano | Aura Counselling" />
        <meta
          property="og:description"
          content="Counselling gratuito in italiano online e in presenza a Zurigo con sessione introduttiva gratuita. Sostegno psicologico in italiano a Zurigo e in tutta la Svizzera con supporto personalizzato per il tuo benessere."
               />
        <meta property="og:image" content={`${SITE_BASE_URL}/aura-logo-no-stripes.webp`} />
        <meta property="og:image:type" content="image/webp" />
        <meta property="og:image:width" content="500" />
        <meta property="og:image:height" content="500" />
        <meta property="og:url" content={canonicalUrl} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Counselling Professionale in Italiano | Aura Counselling" />

        <meta
          name="twitter:description"
          content="Counselling gratuito in italiano a Zurigo e online. Sostegno psicologico in italiano in Svizzera con sessione introduttiva gratuita e supporto personalizzato."
        />
        <meta name="twitter:image" content={`${SITE_BASE_URL}/aura-logo-no-stripes.webp`} />
        <link rel="canonical" href={canonicalUrl} />
      </Helmet>
      <HreflangTags currentPath={CANONICAL_ROUTES.home} />
      <LocalBusinessSchema />
      <ServiceSchema />
      <PersonSchema />
      <BreadcrumbSchema items={[
        { name: 'Home', path: '/' }
      ]} />
      {/* Navigation */}
      <NavigationBanner currentPage="home" />

      {/* Hero Section */}
      <section id="home" className="relative min-h-screen flex items-center justify-center bg-gradient-to-br from-secondary/10 via-primary/8 to-secondary/5">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-20"
          style={{
            backgroundImage:
              "image-set(url('/hero-session.webp') type('image/webp'), url('/hero-session.jpg') type('image/jpeg'))",
          }}
        ></div>
        <div className="absolute inset-0 bg-gradient-to-b from-background/30 via-background/20 to-background/40"></div>
        <div className="relative container px-6 py-32 text-center">
          <picture>
            <source srcSet="/aura-logo-no-stripes.webp" type="image/webp" />
            <img src="/aura-logo-no-stripes.png" alt="Aura Counselling Logo" width={256} height={256} className="w-64 h-auto mx-auto mb-8 animate-fade-in" />
          </picture>
          <h1 className="text-4xl md:text-6xl font-bold mb-8 leading-tight whitespace-pre-line md:whitespace-normal" style={{color: '#074882'}}>
            È un piacere accoglierti
            {"\n"}
            nel tuo spazio di crescita
          </h1>
          <p className="text-2xl md:text-4xl italic mb-6 font-serif text-primary">
            dove ogni passo risveglia la tua consapevolezza interiore
          </p>
        </div>
      </section>

      {/* Quote Section */}
      <section className="py-16 bg-background">
        <div className="container px-6">
          <div className="max-w-4xl mx-auto text-center">
            <blockquote className="text-2xl md:text-3xl font-serif italic leading-relaxed mb-4" style={{color: 'oklch(0.55 0.12 285)'}}>
              "Nel profondo di noi stessi troviamo<br />infinite possibilità"
            </blockquote>
            <p className="text-base" style={{color: 'oklch(0.55 0.12 285)'}}>Emily Dickinson</p>
          </div>
        </div>
      </section>

      {/* Cos'è il Counselling */}
      <section id="counselling" className="pt-32 pb-16 bg-muted/30">
        <div className="container px-6">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-4xl md:text-5xl font-bold mb-12 text-center font-serif">COS'È IL COUNSELLING</h2>
            
            <div className="prose prose-lg max-w-none text-center mb-12">
              <p className="text-xl leading-relaxed" style={{color: '#074882'}}>
                Il counselling è una relazione di aiuto professionale che, attraverso ascolto attivo e dialogo, sostiene la persona nel riconoscere i propri bisogni e valori. Il percorso favorisce l'ampliamento delle prospettive personali, aiuta a dare chiarezza e significato alle esperienze vissute e permette di entrare in contatto con le proprie risorse interiori, affrontando il cammino personale con maggiore consapevolezza, determinazione e apertura a nuove possibilità.
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 gap-8 mt-16">
              <Card className="border-none shadow-lg hover:shadow-xl transition-shadow">
                <CardHeader>
                  <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mb-4">
                    <Heart className="w-6 h-6 text-primary" />
                  </div>
                  <h2 className="text-2xl font-serif font-semibold">Benefici</h2>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-3 text-xl" style={{color: '#074882'}}>
                    <li className="flex items-start gap-2">
                      <span className="text-primary mt-1">•</span>
                      <span>Maggiore consapevolezza di sé</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-primary mt-1">•</span>
                      <span>Gestione dello stress e delle emozioni</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-primary mt-1">•</span>
                      <span>Miglioramento delle relazioni</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-primary mt-1">•</span>
                      <span>Supporto nelle scelte di vita</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-primary mt-1">•</span>
                      <span>Crescita personale e professionale</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>

              <Card className="border-none shadow-lg hover:shadow-xl transition-shadow">
                <CardHeader>
                  <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mb-4">
                    <User className="w-6 h-6 text-primary" />
                  </div>
                  <h2 className="text-2xl font-serif font-semibold">A chi si rivolge</h2>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-3 text-xl" style={{color: '#074882'}}>
                    <li className="flex items-start gap-2">
                      <span className="text-primary mt-1">•</span>
                      <span>Chi affronta momenti di cambiamento</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-primary mt-1">•</span>
                      <span>Chi cerca orientamento nelle scelte</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-primary mt-1">•</span>
                      <span>Chi desidera migliorare le relazioni</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-primary mt-1">•</span>
                      <span>Chi vuole conoscersi meglio</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-primary mt-1">•</span>
                      <span>Chi cerca equilibrio e benessere</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Quote Section 2 */}
      <section className="py-24 bg-gradient-to-r from-primary/12 via-secondary/8 to-primary/10">
        <div className="container px-6">
          <div className="max-w-4xl mx-auto text-center">
            <blockquote className="text-2xl md:text-3xl font-serif italic leading-relaxed mb-4" style={{color: 'oklch(0.55 0.12 285)'}}>
              "La libertà è la capacità dell'uomo di fare un passo oltre la propria situazione"
            </blockquote>
            <p className="text-lg mt-6" style={{color: 'oklch(0.55 0.12 285)'}}>Rollo May</p>
          </div>
        </div>
      </section>

      {/* Servizi */}
      <section id="servizi" className="pt-32 pb-24 bg-background">
        <div className="container px-6">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-4xl md:text-5xl font-bold mb-6 text-center font-serif">I NOSTRI SERVIZI: I TUOI PERCORSI POSSIBILI</h2>
            <p className="text-center mb-16 text-2xl md:text-3xl font-medium" style={{color: 'oklch(0.55 0.12 285)'}}>Ogni viaggio nasce nel momento in cui ti scegli: inizia oggi il tuo percorso di crescita personale e scopri le risorse che hai sempre avuto dentro di te.</p>
            
            <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
              <IntroConsultationCard
                priceLabel={SESSION_PRICE_INTRO}
                onBook={() => navigateToBooking("intro", true)}
              />

              <Card className="border-none shadow-lg hover:shadow-xl transition-all hover:-translate-y-1 flex flex-col">
                <div className="aspect-[4/3] relative overflow-hidden">
                  <picture>
                    <source media="(max-width: 768px)" srcSet="/incontro-individuale-real-mobile.webp" type="image/webp" />
                    <source media="(max-width: 768px)" srcSet="/incontro-individuale-real-mobile.jpg" type="image/jpeg" />
                    <source srcSet="/incontro-individuale-real.webp" type="image/webp" />
                    <img 
                      src="/incontro-individuale-real.jpg" 
                      alt="Incontro individuale di counselling" 
                      width={400}
                      height={300}
                      loading={imageLoading}
                      decoding="async"
                      className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                    />
                  </picture>
                </div>
                <CardHeader>
                  <h2 className="text-2xl font-serif font-semibold">Incontro in Presenza</h2>
                </CardHeader>
                <CardContent className="flex flex-col flex-1">
                  <p className="text-lg text-muted-foreground mb-4">
                    Un percorso dedicato interamente a te, per esplorare le tue esigenze e trovare le tue risorse interiori.
                  </p>
                  <p className="text-lg text-muted-foreground mb-6">
                    Gli incontri si svolgono a Zurigo, in lingua italiana.
                  </p>
                  <div className="mt-auto flex flex-col gap-4">
                    <div className="flex items-center gap-2 text-primary font-semibold text-lg">
                      <Clock className="w-5 h-5 shrink-0" />
                      <span>60 minuti - {SESSION_PRICE_PRESENCE}</span>
                    </div>
                    <Button
                      size="lg"
                      onClick={() => navigateToBooking("presence", true)}
                      className="text-base px-10 py-6 uppercase tracking-wide"
                    >
                      Prenota qui
                    </Button>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-none shadow-lg hover:shadow-xl transition-all hover:-translate-y-1 flex flex-col">
                <div className="aspect-[4/3] relative overflow-hidden">
                  <picture>
                    <source media="(max-width: 768px)" srcSet="/incontro-online-real-mobile.webp" type="image/webp" />
                    <source media="(max-width: 768px)" srcSet="/incontro-online-real-mobile.jpg" type="image/jpeg" />
                    <source srcSet="/incontro-online-real.webp" type="image/webp" />
                    <img 
                      src="/incontro-online-real.jpg" 
                      alt="Incontro online di counselling" 
                      width={400}
                      height={300}
                      loading={imageLoading}
                      decoding="async"
                      className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                    />
                  </picture>
                </div>
                <CardHeader>
                  <h2 className="text-2xl font-serif font-semibold">Incontro Online</h2>
                </CardHeader>
                <CardContent className="flex flex-col flex-1">
                  <p className="text-lg text-muted-foreground mb-4">
                    Un percorso altrettanto personalizzato e profondo, con la comodità e la flessibilità della videochiamata per ricevere sostegno psicologico online in italiano ovunque ti trovi.
                  </p>
                  <p className="text-lg text-muted-foreground mb-6">
                    Ovunque tu sia, il supporto di cui hai bisogno è a portata di click.
                  </p>
                  <div className="mt-auto flex flex-col gap-4">
                    <div className="flex items-center gap-2 text-primary font-semibold text-lg">
                      <Clock className="w-5 h-5 shrink-0" />
                      <span>60 minuti - {SESSION_PRICE_ONLINE}</span>
                    </div>
                    <Button
                      size="lg"
                      onClick={() => navigateToBooking("online", true)}
                      className="text-base px-10 py-6 uppercase tracking-wide"
                    >
                      Prenota qui
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Blog Highlights */}
      <section className="py-24 bg-muted/30">
        <div className="container px-6">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-center font-serif mb-6">Dal nostro blog</h2>
            <p className="text-center text-lg text-muted-foreground max-w-3xl mx-auto">
              Approfondisci temi di counselling e benessere con articoli scritti per chi cerca un supporto professionale in lingua italiana.
            </p>
            <div className="grid gap-8 md:grid-cols-2 mt-12">
              {featuredBlogPosts.map(post => {
                const articlePath = buildBlogArticleRoute(post.slug);

                return (
                  <Card key={post.slug} className="border-none shadow-lg h-full">
                    <CardHeader>
                      <CardTitle className="text-2xl font-serif leading-snug">
                        <a
                          href={articlePath}
                          onClick={event => handleAnchorNavigation(event, () => setLocation(articlePath))}
                          className="hover:text-primary transition-colors"
                        >
                          {post.title}
                        </a>
                      </CardTitle>
                      <p className="text-sm text-muted-foreground">
                        Pubblicato il {new Date(post.datePublished).toLocaleDateString("it-IT", { year: "numeric", month: "long", day: "numeric" })}
                      </p>
                    </CardHeader>
                    <CardContent className="space-y-4 text-muted-foreground">
                      <p>{post.description}</p>
                      <a
                        href={articlePath}
                        onClick={event => handleAnchorNavigation(event, () => setLocation(articlePath))}
                        className="inline-flex items-center gap-2 text-primary font-semibold hover:underline"
                      >
                        Leggi l'articolo
                        <span aria-hidden="true">→</span>
                      </a>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
            <div className="text-center mt-12">
              <Button
                asChild
                className="font-semibold"
              >
                <a href={CANONICAL_ROUTES.blog} onClick={event => handleAnchorNavigation(event, () => setLocation(CANONICAL_ROUTES.blog))}>
                  Visita il blog
                </a>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Final */}
      <section className="py-24 bg-gradient-to-br from-secondary/12 via-primary/10 to-secondary/8">
        <div className="container px-6">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-6 font-serif">La versione migliore di te ti sta aspettando</h2>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-foreground/5 border-t border-border py-16">
        <div className="container px-6">
          <div className="max-w-5xl mx-auto">
            <div className="grid md:grid-cols-3 gap-12 mb-12">
              <div>
                <picture>
                  <source srcSet="/aura-logo-no-stripes.webp" type="image/webp" />
                  <img 
                    src="/aura-logo-no-stripes.png" 
                    alt="Aura Counselling" 
                    width={64} 
                    height={64} 
                    loading={imageLoading}
                    decoding="async"
                    className="h-16 w-auto" 
                  />
                </picture>
              </div>
              
              <div>
                <h4 className="font-semibold mb-4 uppercase tracking-wide text-sm">Menu</h4>
                <div className="space-y-2">
                  <a
                    href={homeHrefForSection("home")}
                    onClick={(event) => handleAnchorNavigation(event, () => scrollToSection("home"))}
                    className="block text-sm text-muted-foreground hover:text-primary transition-colors"
                  >
                    Home
                  </a>
                  <a
                    href={homeHrefForSection("counselling")}
                    onClick={(event) => handleAnchorNavigation(event, () => scrollToSection("counselling"))}
                    className="block text-sm text-muted-foreground hover:text-primary transition-colors"
                  >
                    Counselling
                  </a>
                  <a
                    href={homeHrefForSection("servizi")}
                    onClick={(event) => handleAnchorNavigation(event, () => scrollToSection("servizi"))}
                    className="block text-sm text-muted-foreground hover:text-primary transition-colors"
                  >
                    Servizi
                  </a>
                  <a
                    href={CANONICAL_ROUTES.chiSono}
                    onClick={(event) => handleAnchorNavigation(event, () => setLocation(CANONICAL_ROUTES.chiSono))}
                    className="block text-sm text-muted-foreground hover:text-primary transition-colors"
                  >
                    Chi Sono
                  </a>
                  <a
                    href={CANONICAL_ROUTES.blog}
                    onClick={(event) => handleAnchorNavigation(event, () => setLocation(CANONICAL_ROUTES.blog))}
                    className="block text-sm text-muted-foreground hover:text-primary transition-colors"
                  >
                    Blog
                  </a>
                  <a
                    href={CANONICAL_ROUTES.testimonianze}
                    onClick={(event) => handleAnchorNavigation(event, () => setLocation(CANONICAL_ROUTES.testimonianze))}
                    className="block text-sm text-muted-foreground hover:text-primary transition-colors"
                  >
                    Testimonianze
                  </a>
                  <a
                    href={CANONICAL_ROUTES.prenota}
                    title="Sostegno psicologico in italiano a Zurigo"
                    onClick={(event) =>
                      handleAnchorNavigation(event, () => navigateToBooking("online", false))
                    }
                    className="block text-sm text-muted-foreground hover:text-primary transition-colors"
                  >
                    Prenotazione
                  </a>
                </div>
              </div>
            </div>

            <div className="border-t border-border pt-8 text-center">
              <p className="text-sm text-muted-foreground">
                © {new Date().getFullYear()} Aura Counselling. Tutti i diritti riservati.
              </p>
            </div>
          </div>
        </div>
      </footer>

      {/* Scroll to Top Button */}
      {showScrollTop && (
        <button
          onClick={scrollToTop}
          className="fixed bottom-8 right-8 w-12 h-12 bg-foreground text-background rounded-full shadow-lg hover:shadow-xl transition-all hover:scale-110 flex items-center justify-center z-40"
          aria-label="Torna su"
        >
          <ArrowUp className="w-6 h-6" />
        </button>
      )}
    </div>
  );
}
